import React, { useEffect } from 'react';
import {
  useUser,
  useWallet,
  useMarketData,
  useLoading,
  useError,
  useProfitRecords,
  useUserProfitStats,
  useFetchUserProfitStats,
  useIsFetchingUserProfit,
} from '../stores/useAppStore';
import { ProfitRecord } from '../types';
import QuantAccount from './QuantAccount';
import MyAccount from './MyAccount';
import GlassCard from './ui/GlassCard';
import AnimatedTitle from './ui/AnimatedTitle';
import MarketDataList from './MarketDataList';
import StaggeredAnimation from './ui/StaggeredAnimation';
import ScrollReveal from './ui/ScrollReveal';
import TelegramAvatar from './ui/TelegramAvatar';
// import PriceAlertsSection from "./PriceAlertsSection"; // 已隐藏
import HiddenMenu from './HiddenMenu';

const Dashboard: React.FC = () => {
  // 从 Zustand store 获取状态
  const user = useUser();
  const wallet = useWallet();
  const marketData = useMarketData();
  const loading = useLoading();
  const error = useError();
  const profitRecords = useProfitRecords();
  const userProfitStats = useUserProfitStats();
  const fetchUserProfitStats = useFetchUserProfitStats();
  const userProfitLoading = useIsFetchingUserProfit();

  // 获取用户收益数据
  useEffect(() => {
    if (user?.id) {
      fetchUserProfitStats();
    }
  }, [user?.id, fetchUserProfitStats]);

  // 计算统计数据 - 优先使用用户收益数据，否则使用量化账户数据
  const totalBalance = userProfitStats?.totalAssets || wallet?.balance || 0;
  const totalProfit =
    userProfitStats?.totalProfit ||
    profitRecords.reduce((sum: number, record: ProfitRecord) => sum + record.totalProfit, 0);
  const dailyProfit =
    userProfitStats?.todayProfit ||
    (profitRecords.length > 0 ? profitRecords[profitRecords.length - 1].dailyProfit : 0);
  const profitRate = userProfitStats?.profitRate || (totalBalance > 0 ? (totalProfit / totalBalance) * 100 : 0);

  return (
    <div className="pt-0 px-4 pb-4 space-y-6 relative">
      <HiddenMenu />

      <StaggeredAnimation delay={300} animation="fadeInUp">
        {/* 用户信息 */}
        <GlassCard className="p-6 mb-6">
          <div className="flex items-center justify-start gap-4">
            {/* 用户头像 */}
            <TelegramAvatar
              photoUrl={user?.photo_url}
              firstName={user?.first_name}
              lastName={user?.last_name}
              username={user?.username}
              isPremium={user?.is_premium}
              size="lg"
              animated={false}
              showStatus={false}
              showPremium={true}
            />

            {/* 用户信息 */}
            <div className="text-left">
              {/* 用户姓名 */}
              <div className="flex items-center gap-2 mb-1">
                <p className="text-white/90 text-lg font-semibold">
                  {user?.first_name} {user?.last_name}
                </p>
                {user?.is_premium && (
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>

              {/* 用户名 */}
              {user?.username && <p className="text-sm text-white/60">@{user.username}</p>}
            </div>
          </div>
        </GlassCard>

        {/* 价格警报翻滚提示 - 已隐藏 */}
        {/* <PriceAlertsSection /> */}

        {/* 错误提示 */}
        {error && (
          <GlassCard className="p-4 border-red-500/30 mb-6">
            <p className="text-red-400 text-sm">{error}</p>
          </GlassCard>
        )}
      </StaggeredAnimation>

      {/* 我的账户 - 滚动触发 */}
      <ScrollReveal animation="fadeInUp" delay={200}>
        <MyAccount
          totalAssets={totalBalance}
          totalProfit={totalProfit}
          todayProfit={dailyProfit}
          profitRate={profitRate}
          loading={loading || userProfitLoading}
        />
      </ScrollReveal>

      {/* 量化账户 - 滚动触发 */}
      <ScrollReveal animation="fadeInUp" delay={300}>
        <QuantAccount loading={loading} />
      </ScrollReveal>

      {/* 市场数据 - 滚动触发 */}
      <ScrollReveal animation="fadeInUp" delay={500}>
        <GlassCard className="p-6 overflow-visible">
          <AnimatedTitle variant="neon" size="lg" className="mb-6">
            热门币种 (现货交易)
          </AnimatedTitle>
          <MarketDataList marketData={marketData} loading={loading} />
        </GlassCard>
      </ScrollReveal>
    </div>
  );
};

export default Dashboard;
